import React, { forwardRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-regular-svg-icons";
import {
  BaseLogo,
  ImageRef,
  LogoType,
} from "../../../core/models/entities/BaseLogo";
import { AwaitingImageBadge } from "./AwaitingImageBadge";
import { isBlankDataUri } from "../../../hooks/useHealPendingHighRes";

interface LogoImageProp extends React.ImgHTMLAttributes<HTMLImageElement> {
  logo: BaseLogo;
  useHighRes?: boolean;
  useDigit?: boolean;
  useTrueColors?: boolean;
  // Opt in to render a NameLogo smaller when its heightMm is below 60. Only
  // useful in preview contexts (lists, cards, detail dialogs). Do NOT enable
  // for the design marker, the logo editor canvas, or any context where the
  // image must occupy its actual physical placement footprint.
  scaleNameByHeight?: boolean;
  // Opt in (detail dialogs only): paint the cached low-res immediately and
  // fade the high-res in on load, instead of showing the low-res stretched
  // to detail size until the high-res src happens to swap in ("grainy then
  // sharp"). When the backend has no real high-res yet (fresh clone:
  // highResUrl falls back to lowResUrl), the low-res renders slightly
  // blurred so it reads as a deliberate placeholder rather than pixelation.
  // Default off = exact previous behavior for all other consumers. NOTE: in
  // progressive mode a forwarded ref attaches to the LOW-res base img - do not
  // combine with code that measures naturalWidth/naturalHeight via the ref.
  progressiveHighRes?: boolean;
  // Flag REACT_APP_LOGO_POPUP_FAST (detail dialog only, with
  // progressiveHighRes): while the real high-res is still pending, paint the
  // cached low-res CRISP in the same two-layer frame the crossfade uses,
  // instead of the blurred single <img>. The base layer is then the same
  // element before and after the high-res lands, so the arrival is a fade-in
  // on top - never a repaint of the picture the user is looking at. Default
  // off = today's blur placeholder.
  noPendingBlur?: boolean;
}

// NameLogos should preview at a size that reflects their physical print height.
// Linear scale from 10mm → 0.3 up to 60mm → 1.0. Heights above 60mm render full
// size; heights below 10mm clamp to 0.3. Other logo types are unaffected.
function getNameLogoScale(logo: BaseLogo): number {
  if (logo.logoType !== LogoType.NameLogo) return 1;
  const heightMm = logo.heightMm ?? 60;
  if (heightMm >= 60) return 1;
  return Math.max(0.3, 0.3 + ((heightMm - 10) / 50) * 0.7);
}

export const LogoImage = forwardRef<HTMLImageElement, LogoImageProp>(
  (props, ref) => {
    const {
      logo,
      useHighRes,
      useDigit,
      useTrueColors,
      scaleNameByHeight,
      progressiveHighRes,
      noPendingBlur,
      ...rest
    } = props;

    // Hook must run unconditionally (before any early return).
    const [highResLoaded, setHighResLoaded] = useState(false);

    const { lowResUrl, highResUrl, digitUrl, trueColorsUrl } =
      logo.displayImage ?? ({} as ImageRef);

    rest.src =
      useTrueColors && trueColorsUrl
        ? trueColorsUrl
        : useDigit && digitUrl
          ? digitUrl
          : useHighRes && highResUrl
            ? highResUrl
            : lowResUrl;

    rest.alt = logo.title;

    // Progressive high-res (soft placeholder + crossfade) is meaningful ONLY
    // for ImageLogo clones: their highResUrl legitimately starts equal to
    // lowResUrl and is later replaced by a distinct, genuinely higher-res
    // render fetched in the background. For the server-baked logo types
    // (NumberLogo, NameLogo, TextLogo, SymbolLogo) highResUrl === lowResUrl is a
    // PERMANENT end state — there is never a sharper render to fade in — so
    // progressive mode would blur them forever (the number-preview regression).
    // Those types must render sharp: plain <img>, no blur, no crossfade.
    // Progressive mode also only applies to the plain high-res path (not digit/
    // trueColors variants) and needs both URLs to exist.
    const progressive =
      !!progressiveHighRes &&
      logo.logoType === LogoType.ImageLogo &&
      !!useHighRes &&
      !useDigit &&
      !useTrueColors &&
      !!highResUrl &&
      !!lowResUrl;
    // Fresh ImageLogo clones have no real high-res yet - the mapper falls back
    // to highResUrl === lowResUrl. Blur the stretched low-res slightly so it
    // reads as "loading", not pixelation; a later refetch with a real
    // high-res re-renders into the crossfade branch below.
    const highResPending = progressive && highResUrl === lowResUrl;

    // Recovered logos keep their serial number but have no processed image
    // until a background worker fills it in (awaitingImage) — every image URL
    // is null meanwhile. Render a neutral placeholder instead of a broken
    // <img>. Logos that have an image render exactly as before.
    // A blank data-URI ("data:image/png;base64, " — the API's prefix around an
    // empty live render of a seconds-old serial) is equally unloadable and
    // would paint the browser's broken-image glyph; treat it as missing so the
    // user sees this placeholder while the background heal polls for the real
    // render.
    if (logo.awaitingImage || !rest.src || isBlankDataUri(rest.src)) {
      return (
        <div
          className={`flex flex-col items-center justify-center gap-1.5 rounded border border-dashed border-gray-200 p-3 ${
            rest.className ?? ""
          }`}
          title={rest.alt}
        >
          <FontAwesomeIcon
            icon={faImage}
            className="text-xl text-gray-600 opacity-40"
          />
          {logo.awaitingImage && <AwaitingImageBadge />}
        </div>
      );
    }

    const scale = scaleNameByHeight ? getNameLogoScale(logo) : 1;
    let img = <img ref={ref} {...rest} />;
    if (highResPending && !noPendingBlur) {
      // Deliberate soft placeholder while the real high-res is produced.
      img = (
        <img
          ref={ref}
          {...rest}
          style={{ ...rest.style, filter: "blur(4px)" }}
        />
      );
    } else if (progressive) {
      // Two layers: the cached low-res paints immediately underneath, and the
      // high-res fades in on top once loaded - never a blank or grainy jump.
      // noPendingBlur while pending: base layer only (crisp low-res), the
      // top layer mounts when a distinct high-res exists and fades in on
      // load. Without noPendingBlur this branch is never reached while
      // pending, so the top layer is always rendered as before.
      img = (
        <span className="relative inline-flex justify-center items-center">
          <img ref={ref} {...rest} src={lowResUrl} />
          {!highResPending && (
            <img
              {...rest}
              src={highResUrl}
              alt=""
              aria-hidden
              className={`${rest.className ?? ""} absolute inset-0 m-auto w-full h-full pointer-events-none transition-opacity duration-300 ${
                highResLoaded ? "opacity-100" : "opacity-0"
              }`}
              onLoad={() => setHighResLoaded(true)}
            />
          )}
        </span>
      );
    }
    if (scale < 1) {
      // Wrap the img so the base NameLogo scale lives on the wrapper and any
      // hover-scale utilities on the img (e.g. `hover:scale-[3]`) still apply.
      // The two transforms compose multiplicatively.
      return (
        <span
          style={{
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            transform: `scale(${scale})`,
            transformOrigin: "center",
          }}
        >
          {img}
        </span>
      );
    }
    return img;
  },
);
